import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { CircularProgress, DialogContent, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  inviteShareCandidateUsers,
  getJobByJobCode,
  getRecruiterSMTPAccounts,
  ISMTPAccountsResponse,
  EvaluationTamplateDTO,
  getCandidateSummary,
  updateCandidateSubmission,
} from 'wisestep-api';
import {
  ControlledTextArea,
  formattedDateISO,
  WsButton,
  WsColors,
  WsDialog,
  WsDialogSize,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import {
  useDefaultSmtpEmail,
  useSurveyLogoUrl,
} from '../../../components/SurveyBuilder/SurveyCommonMethods';
import { useUserContext } from '../../../context/UserContext';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { evaluationAmplitudeEvents } from '../../../features/conversationWidget/utils/rtrHelper';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { useCandidateViewData } from '../../candidateLayout/utils/CandidateViewContext';

import ControlledShareNameAndEmailInput from './ControlledShareNameAndEmailInput';
import DefaultEmailModal from './DefaultEmailModal';
import ShareCandidateConfirmation from './ShareCandidateConfirmation';

interface IShareCandidate {
  interactionId: string;
  onClose: () => void;
  reset: () => void;
  jobTitle: string;
  name: string;
  jobCode: string;
  evalTempData?: {
    createdBy: string;
    createdOn: string;
    id: string;
    jobId: string;
    recruiterName: string;
    templateId: string;
    templateName: string;
  };
  selectedTempData?: EvaluationTamplateDTO;
  templateType?: string | '';
}

interface JobCandidateInteractionTemp {
  id: string;
  applicationStage: string;
  applicationState: string;
  candidateNotes: { createdAt: string; createdBy?: string; notes: string }[];
  stageAndStateActions?: {
    stage: string;
    state: string;
    id: string;
    updatedBy: string;
    updatedOn: string;
  }[];
  stageDate?: string | null;
  candidateId: string;
  name: string;
  page: number;
}

const modalDataMapping = {
  evaluation: {
    heading: 'Evaluate Candidate',
    showInfoIcon: false,
    shareStatus: 'viewAndEval',
  },
};

const jobCandidateInteraction_temp: JobCandidateInteractionTemp = {
  id: '',
  applicationStage: '',
  applicationState: '',
  candidateNotes: [{ notes: '', createdBy: '', createdAt: '' }],
  stageAndStateActions: [
    {
      stage: '',
      state: '',
      id: '',
      updatedBy: '',
      updatedOn: new Date().toString(),
    },
  ],
  candidateId: '',
  name: '',
  page: 0,
};

const ShareCandidate = ({
  interactionId,
  onClose,
  reset,
  jobTitle,
  name,
  jobCode,
  evalTempData,
  selectedTempData,
  templateType = 'evaluation',
}: IShareCandidate) => {
  const navigate = useNavigate();
  const { candidateViewData } = useCandidateViewData();
  const [jobId, setJobId] = useState<string | null>(null);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { defaultSmtpEmailValue, showEmailPopupFlag, preferredProviderId } = useDefaultSmtpEmail();

  const templateData = modalDataMapping[templateType];
  const shareStatus = templateData.shareStatus;
  const [invalidEmailList, setInvalidEmailList] = useState<
    {
      name: string;
      email: string;
    }[]
  >([]);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState(false);
  const [defaultSmtpEmail, setDefaultSmtpEmail] = useState<string | undefined>(
    defaultSmtpEmailValue
  );
  const [showEmailPopup, setShowEmailPopup] = useState<boolean>(showEmailPopupFlag);
  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);
  const [isLoading, setIsLoading] = useState(false);
  const user = useUserContext();
  const fileUrlValue = useSurveyLogoUrl();
  // const linkValidator = useValidLinkContext();
  const workspace = useWorkspaceContext();

  const fullUrl = `${window.location.href}&corphiringTemplate=evalutionTemplate`;

  const methods = useForm({
    resolver: yupResolver(
      object().shape({
        contact: array()
          .of(
            object().shape({
              name: string().required('Please enter name'),
              email: string().required('Please enter email').email('Email is invalid'),
            })
          )
          .required('Please enter contact details properly'),
      })
    ),
  });

  useEffect(() => {
    (async () => {
      const smtpResponse = await getRecruiterSMTPAccounts();
      if (smtpResponse && smtpResponse.data) {
        const allEmails: ISMTPAccountsResponse[] = smtpResponse.data;
        let defaultEmail = allEmails.find((email) => email.isDefault);
        if (allEmails.length === 0) {
          setShowEmailPopup(true);
        } else if (!defaultEmail) {
          defaultEmail = allEmails[0];
        }
        setDefaultSmtpEmail(defaultEmail?.fromEmail);
      }
    })();
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (!isEmpty(shareStatus)) {
      const candidateInfo = {
        candidates: data.contact,
        description: data.description,
        status: shareStatus,
      };
      if (shareStatus === 'viewAndEval') {
        const job = await getJobByJobCode(jobCode);
        setJobId(job.data.id);
      }
      inviteUser(candidateInfo);
      evaluationAmplitudeEvents(
        'eval_request_sent',
        'Sending evaluation link to the evaluators',
        workspace
      );
    } else {
      setNotification(warning('Please enter name and email address'));
    }
  };

  const getCandidatesNameList = (candidateList) => {
    let nameList = '';
    candidateList.map(
      (candidateObj) => (nameList += `${nameList != '' ? ', ' : ''}${candidateObj?.name}`)
    );
    return nameList;
  };

  const inviteUser = async (data) => {
    setIsLoading(true);
    let jobDataId = jobId;
    const currentJob = candidateViewData?.candidate?.allCandidateSubmissions?.filter(
      (submssionObj) => submssionObj?.jobCode === jobCode
    );
    const { modifiedOn = '', id = '' } = currentJob?.[0] ? currentJob[0] : {};

    const submissionResponse = await getCandidateSummary(interactionId ?? '');
    const { applicationStage = '', applicationState = '' } = submissionResponse?.data || {};

    const { defaultStage = '', defaultStatus = '' } = selectedTempData?.associateStageStatusData
      ? selectedTempData.associateStageStatusData
      : {};
    if (data.status === 'viewAndEval') {
      const tempNameVal = evalTempData?.templateName;
      const nameList = getCandidatesNameList(data?.candidates);
      const recruiterName = user?.userDTO?.displayName ?? '';
      let editedNote = '';
      jobCandidateInteraction_temp.applicationStage = defaultStage
        ? defaultStage
        : applicationStage;
      jobCandidateInteraction_temp.applicationState = defaultStatus
        ? defaultStatus
        : applicationState;
      jobCandidateInteraction_temp.id = interactionId as string;
      // jobCandidateInteraction_temp.stageDate = modifiedOn ? modifiedOn : ''; //formattedDateISO(new Date());
      editedNote = `Evaluation Request with template ${tempNameVal} was requested to ${nameList}`;
      jobCandidateInteraction_temp.candidateNotes = [
        {
          notes: editedNote,
          createdBy: recruiterName as string,
          createdAt: formattedDateISO(new Date()),
        },
      ];
      jobCandidateInteraction_temp.stageAndStateActions = [
        {
          stage: defaultStage ? defaultStage : applicationStage,
          state: defaultStatus ? defaultStatus : applicationState,
          id: id ? id : '',
          updatedBy: (recruiterName as string) ?? '',
          updatedOn: modifiedOn ? modifiedOn : '',
        },
      ];
      // await updateJobCandidateNotesStateStage(jobCandidateInteraction_temp);
      await updateCandidateSubmission([jobCandidateInteraction_temp]);
    }

    if (isEmpty(jobDataId)) {
      const res = await getJobByJobCode(jobCode);
      jobDataId = res.data.id;
    }
    if (data && jobDataId) {
      const res = await inviteShareCandidateUsers({
        to: data.candidates,
        subject: `Evaluation for ${jobTitle} || ${name}`,
        body: data.description,
        interactionId: interactionId,
        jobId: jobDataId,
        evalTemplateId: evalTempData?.templateId || null,
        sharedCandidateName: name,
        sharedJobTitle: jobTitle,
        recruiterEmail: defaultSmtpEmail ?? '',
        candidiateId: candidateViewData?.candidateId ?? '',
        logoUrl: fileUrlValue ?? '',
        otherInfo: [
          {
            workspaceName: workspace.name,
            workspacePlanType: workspace.planType,
            candidateProfileLink: fullUrl,
          },
        ],
        preferredProviderId: preferredProviderId,
      });
      setInvalidEmailList(res.data.invalidGuest);
      setOpenConfirmationPopup(true);
      setNotification(success('Evaluation request sent to candidate!'));
    }
    onClose();
    setIsLoading(false);
  };

  return (
    <>
      {!openConfirmationPopup ? (
        <WsDialog size={WsDialogSize.SMALL} openDialog={true} onClose={onClose}>
          <Grid container sx={{ backgroundColor: WsColors.TertiaryShades200, pb: 1.3 }}>
            <Grid item xs={8}>
              <Stack direction="row" sx={{ mt: 1, ml: 3 }} spacing={1}>
                <WsTypography display="inline" variant={WsTypoVariants.SUB_TITLE}>
                  {templateData.heading}
                </WsTypography>
                {templateData.showInfoIcon && (
                  <InfoIcon
                    sx={{
                      fontSize: 24,
                      color: WsColors.TheBlues500,
                    }}
                  />
                )}
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <CloseIcon
                onClick={onClose}
                sx={{ float: 'right', mr: 2, fontSize: '23px', mt: 1.5, cursor: 'pointer' }}
              />
            </Grid>
          </Grid>
          <DialogContent>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <ControlledShareNameAndEmailInput name="contact" reset={reset} />
                <Grid container>
                  <Grid item xs={12}>
                    <ControlledTextArea name="description" labelText="Description" minRows={5} />
                  </Grid>
                  <Grid container xs={12} justifyContent="flex-end">
                    <Grid item xs={isLoading ? 7 : 8}></Grid>
                    <Grid item xs={2}>
                      <WsButton onClick={onClose} variant="outlined">
                        Close
                      </WsButton>
                    </Grid>
                    <Grid item xs={isLoading ? 3 : 2}>
                      {/* <WsButton type="submit" disabled={isEmpty(defaultSmtpEmail)}> */}
                      <WsButton
                        type="submit"
                        disabled={isLoading}
                        endIcon={
                          isLoading ? <CircularProgress size={17} color="secondary" /> : <></>
                        }
                      >
                        {isLoading ? 'Sending...' : 'Proceed'}
                      </WsButton>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </DialogContent>
        </WsDialog>
      ) : (
        <ShareCandidateConfirmation
          onClose={onClose}
          isEvalNeeded={shareStatus === 'viewAndEval'}
          invalidEmails={invalidEmailList}
        />
      )}
      {showEmailPopup && (
        <WsDialog openDialog={showEmailPopup} onClose={() => setShowEmailPopup(false)}>
          <DefaultEmailModal
            title="Email not configured"
            text="No email is configured for your Wisestep Account."
            content="To configure your email, please click on 'Configure'"
            confirm={() => {
              setShowEmailPopup(false);
              navigate('/email-settings');
              onClose();
              onWidgetClose();
            }}
            close={() => setShowEmailPopup(false)}
            confirmButtonText="Configure"
          ></DefaultEmailModal>
        </WsDialog>
      )}
    </>
  );
};

export default ShareCandidate;
